import { Backdrop, createTheme, Grid, IconButton, MuiThemeProvider, Typography } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Add as AddIcon, Delete as DeleteIcon } from '@material-ui/icons';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import Select from '../../../components/Form/Select';
import SelectSupplier from '../../../components/Form/SelectSupplier';
import TextField from '../../../components/Form/TextField';
import AlertDialog from '../../../components/ui/AlertDialog';
import ButtonUI from '../../../components/ui/ButtonUI';
import OptionValueApi from '../../../services/OptionValueApi';
import getDefaultGridOptions from '../../../utils/componentUtils';
import { useStyles } from '../Style';
type props =  any;

interface IFormInput {
    requestNumber: any;
    requestType: any;
    supplier: any;
}

const MvmOutgoingRequestList: React.FC<props> = ({ control, title, name, allowAdd, allowDelete, allowInvoiceEdit }) => {

    const classes = useStyles();
    const { t } = useTranslation("Mvm");
    const { t: _t } = useTranslation("General");
    const { t: tval } = useTranslation("Validation");
    const { t: it } = useTranslation("MvmOutgoingRequest");
    const { enqueueSnackbar } = useSnackbar();
    const [circularLoading, setCircularLoading] = useState(false);
    const [requestTypes, setRequestTypes] = useState([]);
    const { fields: _fields, append, remove, update } = useFieldArray({ control, name: name });
    const fields = _fields as Record<string, any>[];
    const refRequestNumber = useRef<HTMLElement>(null);
    const { control: formInvoiceControl, setError: setInvoiceError, clearErrors: clearInvoiceErrors } = useForm({}); //RHF para validar os campos de invoice que estao fora do <form>
    const { control: formControl, handleSubmit, setError, clearErrors, resetField } = useForm<IFormInput>({
        defaultValues: {
            requestNumber: '',
            requestType: '',
            supplier: {}
        }
    });

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: ''
                }
            }
        }
    })

    const columns = [
        {
            name: 'id',
            options: {
                display: false,
            }
        },
        {
            name: 'requestNumber',
            label: String(it('requestNumber')),
            options: {
                setCellHeaderProps: () => ({ align: "center" }),
                setCellProps: () => ({ align: "center" })
            }
        },
        {
            name: 'outgoingRequestType',
            label: String(it('requestType')),

            options: {
                setCellHeaderProps: () => ({ align: "center" }),
                setCellProps: () => ({ align: "center" }),
                customBodyRender: (value: any) => {
                    return it('requestType_' + value);
                }
            }
        },
        {
            name: 'supplierName',
            label: String(_t('supplier')),
            options: {
                sort: false,
                setCellHeaderProps: () => ({ align: "center" }),
                setCellProps: () => ({ align: "center" })
            }
        },
        {
            name: 'invoiceKey',
            label: String(it('invoiceKey')),
            options: {
                display: allowInvoiceEdit,
                sort: false,
                setCellHeaderProps: () => ({ align: "center" }),
                setCellProps: () => ({ align: "center" }),
                customBodyRender: (value: any, tableMeta: any) => (
                    renderInvoiceField(value, tableMeta)
                )
            }
        },
        {
            name: 'invoiceNumber',
            label: String(it('invoiceNumber')),
            options: {
                display: allowInvoiceEdit,
                sort: false,
                setCellHeaderProps: () => ({ align: "center" }),
                setCellProps: () => ({ align: "center" })
            }
        },
        {
            name: "",
            options: {
                setCellProps: () => ({
                    align: "right"
                }),
                disableClick: true,
                sort: false,
                customBodyRender: (value: any, tableMeta: any) => (
                    renderButtons(tableMeta.rowData[0], tableMeta.rowIndex)
                )
            }
        },
    ]

    const renderButtons = (id: any, rowIndex: any) => {
        //Se usuario acabou de incluir este item na lista, ela tem mvmid=0
        let justAdded = fields[rowIndex]['mvmId'] === 0;

        if ((allowAdd && justAdded) || allowDelete)
            return (
                <AlertDialog
                    dialogTitle={t("dialogDeleteTitle")}
                    dialogContentText={t("dialogDeleteContentText")}
                    cancelTextButton={t("cancelDeleteTextButton")}
                    confirmTextButton={t("confirmDeleteTextButton")}
                    confirmDialogFunction={() => removeItemFromList(rowIndex)}
                    Button={
                        <IconButton size='small' edge="end">
                            <DeleteIcon />
                        </IconButton>
                    }
                />
            );
        else
            return (<></>)
    }

    const renderInvoiceField = (value: any, tableMeta: any) => 
    {
        return(
            <TextField 
                name={`invoiceKey_${tableMeta.rowIndex}`}
                control={formInvoiceControl} 
                onChange={(e) => invoiceChange(tableMeta.rowIndex, e.target.value) }
                inputProps={{maxLength: 44}}
                defaultValue={value}
                fullWidth />
        );
    }

    //Atualiza o valor da nota fiscal (id = indice do array de fields, enviado para a funcao update do useFieldArray)
    const invoiceChange = async (id: any, value: any) => {
        let item: any = fields[id];

        if(!(await yupInvoiceValidation(value, id)))
            item.hasError = true;
        else
            item.hasError = false;


        item.invoiceKey = value;
        item.invoiceNumber = value.substring(25, 34);

        update(id, item);
    }

    useEffect(() => {
        OptionValueApi.search("AETipoReqSaida").then((response) => {
            if (response) {
                setRequestTypes(response.map((x: any) => ({code: x.code, label: x.description }) ));
            }
        }).finally(() => {
            // setLoading(false);
        });
    }, []);
    

    const yupValidation = async (data: any) => {
        clearErrors(["requestNumber", "requestType", "supplier"]);
        try {
            const schema = Yup.object().shape({
                requestNumber: Yup.number()
                    .required(tval("requiredField"))
                    .typeError(tval("mustBeNumber")),
                requestType: Yup.object().nullable()
                    .required(tval("requiredField"))
                    .shape({code: Yup.string().required(tval("requiredField"))}),
                supplier: Yup.object().nullable()
                    .required(tval("requiredField"))
                    .shape({code: Yup.string().required(tval("requiredField"))}),
            })

            await schema.validate(data, {
                abortEarly: false,
            })

        } catch (err) {
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach(error => {
                    setError(error.path?.split('.')[0] as "requestNumber" | "requestType" | "supplier", { message: error.message })
                })
            }
            return false;
        }

        return true;
    }

    //Validacao sem uso de Form: personalizado para exibir no campo invoiceKey_x
    const yupInvoiceValidation = async (data: any, rowIndex: number) => {
        clearInvoiceErrors("invoiceKey_" + rowIndex);

        try {
            const schema = Yup.string().nullable()
                .matches(/\b\d{44}\b/, {message: tval("requiredLength44"), excludeEmptyString: true}); //Null OU exatamente 44 numeros
        
            await schema.validate(data, {
                // abortEarly: true
            });
            return true;
        }  catch (err) {
            if (err instanceof Yup.ValidationError){
                setInvoiceError("invoiceKey_" + rowIndex, { message: err.message });            }
            return false;
        }
    }

    const addItemToList = async (data: any) => {
        if(!(await yupValidation(data)))
            return;

        setCircularLoading(true);

        //var requestNumber = refRequestNumber.current;
        if (!data.requestNumber)
        {
            setCircularLoading(false);
            return;
        }

        data.mvmId = 0;
        data.supplierId = data.supplier.code;
        data.supplierName = data.supplier.label;
        data.supplierCorporateName = data.supplier.name;
        data.outgoingRequestType = data.requestType.code;

        // item ja esta na lista?
        var existingItem = false;
        fields.forEach((field) => {
            if ((field as any).requestNumber === data.requestNumber && (field as any).requestTypeCode === data.requestTypeCode){
                existingItem = true;
            }
        })

        if (existingItem) {
            enqueueSnackbar(it("itemAlreadyOnList"), { variant: 'warning' })
            setCircularLoading(false)
            return;
        }

        //Adicionar na lista:
        append(data);
        enqueueSnackbar(it("mvmOutgoingRequestAddSuccess"), { variant: 'success' });

        resetField('requestNumber')
        refRequestNumber.current?.focus();

        setCircularLoading(false);

    }

    const removeItemFromList = (index: number) => {
        remove(index)
    }

    return (
        <Grid style={{ marginTop: 20, width: "98%", marginLeft: 10, marginBottom: 10 }}>
            <Typography className={classes.root} style={{ marginLeft: 0, marginBottom: 10 }} >
                {title ? title : t("outgoingRequests")}
            </Typography>

            <form onSubmit={handleSubmit(addItemToList)}>
            
            {!allowAdd ? <Grid /> : <>

                <Grid container spacing={1} style={{ marginBottom: 10 }}>
                        <Grid item xs={2} >
                            <TextField
                                label={it("requestNumber")}
                                name="requestNumber"
                                control={formControl}
                                inputProps={{maxLength: 12}}
                                inputRef={refRequestNumber}
                                fullWidth />
                        </Grid>

                        <Grid item xs={2}>
                            <Select
                                label={it("requestType")}
                                name="requestType"
                                options={requestTypes}
                                control={formControl}
                                fullWidth />
                        </Grid>

                        <Grid item xs={3} >
                            <SelectSupplier name="supplier" control={formControl} />
                        </Grid>

                        <Grid item xs={1}>
                            <ButtonUI
                                color="primary"
                                type="submit"
                                variant="contained"
                                // onClick={() => AddItemToList()}
                                style={{ maxWidth: '40px', maxHeight: '38px', minWidth: '40px', minHeight: '38px', marginTop: 15, marginLeft: '10px' }}
                                >
                                <AddIcon />
                            </ButtonUI>
                        </Grid>
                        <Grid item xs={4}></Grid>

                </Grid></>
            }
            </form>

            <Backdrop style={{ zIndex: 9999 }} open={circularLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <MuiThemeProvider theme={myTheme} >
                <MUIDataTable
                    title=""
                    data={fields}
                    columns={columns}
                    options={{
                        ...getDefaultGridOptions(_t),
                        serverSide: false,
                        pagination: false,
                        sort: false,
                        selectToolbarPlacement: "none",
                        filter: false,
                        search: false,
                        download: false,
                        print: false,
                        viewColumns: false,
                        selectableRows: 'none',
                    }}
                />
            </MuiThemeProvider>

        </Grid>
    )
}

export default MvmOutgoingRequestList